import { SessionService } from "./SessionService";
import { environment } from "../../environment";
import { Flash } from "../utils/Flash";
import { getBackendDisplayMessage, getBackendMessageKey } from "../utils/backendMessages";

export type RequestOptions = {
  authToken?: string;
  includeSessionAuth?: boolean;
  clearSessionOnUnauthorized?: boolean;
};

const DEBUG_TOAST_DURATION = 5000;
const DEBUG_PAYLOAD_LIMIT = 900;
const SESSION_TIMEOUT_MESSAGE = "Hare Krishna. Your session has expired. Please log in again.";
let isShowingSessionTimeout = false;

const normalizeResponse = <T>(data: T): T => {
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return data;
  }

  const apiRes: any = data;
  const normalized: any = { ...apiRes };

  if (normalized.status == null) {
    if (normalized.statusCode != null) {
      normalized.status = normalized.statusCode;
    } else if (normalized.Status != null) {
      normalized.status = normalized.Status;
    }
  }

  const normalizedMessageKey = getBackendMessageKey(normalized.message, normalized.Message);
  if (normalizedMessageKey) {
    normalized.messageKey = normalizedMessageKey;
  }

  const normalizedMessage = getBackendDisplayMessage(normalized.message, normalized.Message);
  if (normalizedMessage) {
    normalized.message = normalizedMessage;
  }

  if (normalized.data === undefined && normalized.Data !== undefined) {
    normalized.data = normalized.Data;
  }

  if (normalized.token === undefined && normalized.Token !== undefined) {
    normalized.token = normalized.Token;
  }

  return normalized as T;
};

const truncateDebugText = (value: string) =>
  value.length > DEBUG_PAYLOAD_LIMIT
    ? `${value.slice(0, DEBUG_PAYLOAD_LIMIT)}...`
    : value;

const serializeDebugValue = (value: unknown) => {
  if (value == null) {
    return "";
  }

  if (typeof value === "string") {
    return value;
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const buildDebugDescription = (parts: Array<string | undefined>) =>
  parts.filter(Boolean).join("\n\n");

const debugApiRequest = (method: string, endpoint: string, payload?: unknown) => {
  if (!environment.enableApiDebugger) {
    return;
  }

  Flash.info(
    `${method.toUpperCase()} Request`,
    buildDebugDescription([
      `Endpoint: ${endpoint}`,
      payload === undefined ? undefined : `Payload:\n${truncateDebugText(serializeDebugValue(payload))}`,
    ]),
    DEBUG_TOAST_DURATION
  );
};

const debugApiResponse = (method: string, endpoint: string, payload: unknown) => {
  if (!environment.enableApiDebugger) {
    return;
  }

  const normalized = normalizeResponse(payload as any);

  Flash.info(
    `${method.toUpperCase()} Response`,
    buildDebugDescription([
      `Endpoint: ${endpoint}`,
      normalized?.message ? `Message: ${normalized.message}` : undefined,
      `Response:\n${truncateDebugText(serializeDebugValue(normalized))}`,
    ]),
    DEBUG_TOAST_DURATION
  );
};

const debugApiError = (method: string, endpoint: string, error: any) => {
  if (!environment.enableApiDebugger) {
    return;
  }

  const responseData = normalizeResponse(error?.response?.data);
  const message =
    responseData?.message ??
    error?.message ??
    "Request failed";

  Flash.error(
    `${method.toUpperCase()} Error`,
    buildDebugDescription([
      `Endpoint: ${endpoint}`,
      `Message: ${message}`,
      responseData ? `Response:\n${truncateDebugText(serializeDebugValue(responseData))}` : undefined,
    ])
  );
};

const setHeaders = async (options: RequestOptions = {}) => {
  const token =
    options.authToken ??
    (options.includeSessionAuth === false ? "" : await SessionService.getAccessToken());

  const headers: any = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers.Authorization = token;
  }

  return headers;
};

const buildUrl = (endpoint: string) => {
  const baseUrl = environment.apiUrl.replace(/\/+$/, "");
  const path = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
  return `${baseUrl}${path}`;
};

const parseResponseBody = async (response: Response) => {
  const text = await response.text();

  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

const createHttpError = (status: number, data: any) => {
  const error: any = new Error(
    getBackendDisplayMessage(data?.message, data?.Message) ??
      `Request failed with status ${status}`
  );

  error.response = {
    status,
    data,
  };

  return error;
};

const request = async <T>(
  method: "GET" | "POST" | "PUT" | "DELETE",
  endpoint: string,
  payload?: any,
  options?: RequestOptions
): Promise<T> => {
  const headers = await setHeaders(options);
  const response = await fetch(buildUrl(endpoint), {
    method,
    headers,
    body: payload === undefined ? undefined : JSON.stringify(payload),
  });

  const rawData = await parseResponseBody(response);
  const normalizedData = normalizeResponse(rawData as T);

  if ((normalizedData as any)?.status === 440) {
    throw createHttpError(440, normalizedData);
  }

  if (!response.ok) {
    throw createHttpError(response.status, normalizedData);
  }

  return normalizedData;
};

const handleError = async (error: any, options: RequestOptions = {}) => {
  const status = error?.response?.status ?? 0;
  const normalizedResponse = normalizeResponse(error?.response?.data);

  if (error?.response) {
    error.response.data = normalizedResponse;
  }

  const resolvedMessage = getBackendDisplayMessage(
    normalizedResponse?.message,
    normalizedResponse?.Message,
    error?.message
  );
  const resolvedMessageKey = getBackendMessageKey(
    normalizedResponse?.messageKey,
    normalizedResponse?.message,
    normalizedResponse?.Message
  );

  if (resolvedMessage) {
    error.message = resolvedMessage;
  }

  if (resolvedMessageKey) {
    error.messageKey = resolvedMessageKey;
  }

  if (status === 440) {
    if (!isShowingSessionTimeout) {
      isShowingSessionTimeout = true;
      Flash.error("Session Timeout", error.message ?? SESSION_TIMEOUT_MESSAGE);
    }

    try {
      await SessionService.handleSessionExpired();
    } finally {
      isShowingSessionTimeout = false;
    }

    return Promise.reject(error);
  }

  return Promise.reject(error);
};

export const AjaxService = {
  async get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    try {
      debugApiRequest("GET", endpoint);
      const data = await request<T>("GET", endpoint, undefined, options);
      debugApiResponse("GET", endpoint, data);
      return data;
    } catch (error) {
      debugApiError("GET", endpoint, error);
      return handleError(error, options);
    }
  },

  async post<T>(endpoint: string, data: any, options?: RequestOptions): Promise<T> {
    try {
      debugApiRequest("POST", endpoint, data);
      const result = await request<T>("POST", endpoint, data, options);
      debugApiResponse("POST", endpoint, result);
      return result;
    } catch (error) {
      debugApiError("POST", endpoint, error);
      return handleError(error, options);
    }
  },

  async put<T>(endpoint: string, data: any, options?: RequestOptions): Promise<T> {
    try {
      debugApiRequest("PUT", endpoint, data);
      const result = await request<T>("PUT", endpoint, data, options);
      debugApiResponse("PUT", endpoint, result);
      return result;
    } catch (error) {
      debugApiError("PUT", endpoint, error);
      return handleError(error, options);
    }
  },

  async delete<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    try {
      debugApiRequest("DELETE", endpoint);
      const result = await request<T>("DELETE", endpoint, undefined, options);
      debugApiResponse("DELETE", endpoint, result);
      return result;
    } catch (error) {
      debugApiError("DELETE", endpoint, error);
      return handleError(error, options);
    }
  },
};
